/**
 * Admin V3 — Drivers — Approval Workflow Panel (Plan §2.2 M-F-7).
 * Wraps POST /admin/drivers/{id}/approve + reject (ADMIN_DRIVER_APPROVE/REJECT, G3).
 * Phase 4 G5.
 */
import React, { useState } from 'react';
import { ShieldCheck, Check, X as XIcon } from 'lucide-react';
import { SectionCard } from '../../shared/visualTokens';
import { Button } from '../../../components/ui/button';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import usePermissions from '../../shared/hooks/usePermissions';
import { t } from '../../i18n';

// RBAC HIDE gating (P0-1a) — permission strings mirror the backend
// @rbac_permission on POST /admin/drivers/{id}/approve|reject
// (admin_driver_compliance.py). Fail-closed: usePermissions().can() returns
// false whenever the permission set is unresolved, so the mutating affordance
// is hidden. Backend @rbac_permission remains the authoritative gate.
const PERM_APPROVE = 'admin:driver:approve';
const PERM_REJECT = 'admin:driver:reject';

// Surface backend approval errors verbatim — missing/unverified/expired
// documents and invalid-state conflicts — instead of a generic message.
const DOC_LABELS = {
  permis_de_conduire: 'permis de conduire',
  capacite_professionnelle: 'carte professionnelle VTC',
  carte_grise: 'carte grise',
  attestation_assurance: "attestation d'assurance",
  identity_document: "pièce d'identité",
};
const labelDocs = (arr) => (arr || []).map((d) => DOC_LABELS[d] || d).join(', ');
function approvalErrorMessage(err, locale) {
  const detail = err && err.response && err.response.data && err.response.data.detail;
  if (detail && typeof detail === 'object') {
    if (detail.error === 'documents_incomplete') {
      const parts = [];
      if (detail.missing && detail.missing.length) parts.push(`manquants : ${labelDocs(detail.missing)}`);
      if (detail.unverified && detail.unverified.length) parts.push(`non vérifiés : ${labelDocs(detail.unverified)}`);
      if (detail.expired && detail.expired.length) parts.push(`expirés : ${labelDocs(detail.expired)}`);
      return `Documents requis — ${parts.join(' · ')}`;
    }
    if (detail.error === 'requires_legacy_repair') {
      return "Chauffeur hérité (active + pending_backfill) : traiter via la réparation, pas l'approbation normale.";
    }
    if (detail.message) return detail.message;
    if (detail.error) return detail.error;
  }
  if (typeof detail === 'string') return detail;
  return t('compliance.common.error', locale);
}

const DriverApprovalWorkflowPanel = ({ driver, locale = 'fr', onChange }) => {
  const driverId = driver && driver.id;
  const [reason, setReason] = useState('');
  const [feedback, setFeedback] = useState(null);
  const { can } = usePermissions();

  const approve = useAdminMutation(driverId ? `/admin/drivers/${driverId}/approve` : '/noop', 'post');
  const reject  = useAdminMutation(driverId ? `/admin/drivers/${driverId}/reject` : '/noop', 'post');

  const handleApprove = async () => {
    setFeedback(null);
    const res = await approve.mutate({}, {
      onSuccess: () => {
        setFeedback({ kind: 'success', msg: t('compliance.approval.approved.success', locale) });
        if (onChange) onChange();
      },
      onError: (err) => setFeedback({ kind: 'error', msg: approvalErrorMessage(err, locale) }),
    });
    return res;
  };

  const handleReject = async () => {
    setFeedback(null);
    const fd = new FormData();
    fd.append('reason', reason);
    const res = await reject.mutate(fd, {
      onSuccess: () => {
        setFeedback({ kind: 'success', msg: t('compliance.approval.rejected.success', locale) });
        setReason('');
        if (onChange) onChange();
      },
      onError: (err) => setFeedback({ kind: 'error', msg: approvalErrorMessage(err, locale) }),
    });
    return res;
  };

  return (
    <SectionCard
      icon={ShieldCheck}
      iconClass="bg-emerald-100 text-emerald-700"
      title={t('compliance.approval.title', locale)}
    >
      <div className="space-y-4" data-testid="driver-approval-panel">
        {can(PERM_APPROVE) && (
          <div className="flex gap-3">
            <Button
              type="button"
              onClick={handleApprove}
              disabled={approve.loading}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
              data-testid="driver-approve-btn"
            >
              <Check className="h-4 w-4" />
              {t('compliance.approval.action.approve', locale)}
            </Button>
          </div>
        )}

        {can(PERM_REJECT) && (
          <div className="border-t border-slate-200 pt-4">
            <label className="block text-sm text-slate-700 mb-1">
              {t('compliance.approval.field.reason', locale)}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
              rows={2}
              data-testid="driver-reject-reason"
            />
            <Button
              type="button"
              onClick={handleReject}
              disabled={reject.loading || !reason}
              variant="destructive"
              className="mt-3 inline-flex items-center gap-2"
              data-testid="driver-reject-btn"
            >
              <XIcon className="h-4 w-4" />
              {t('compliance.approval.action.reject', locale)}
            </Button>
          </div>
        )}

        {feedback && (
          <div className={`text-sm ${feedback.kind === 'success' ? 'text-emerald-700' : 'text-rose-700'}`}>
            {feedback.msg}
          </div>
        )}
      </div>
    </SectionCard>
  );
};

export default DriverApprovalWorkflowPanel;
